import React from 'react';
import { Box, Typography, Stack, BoxProps, TypographyProps, Tooltip } from '@mui/material';
import { styled, keyframes } from '@mui/system';
import { isMobile } from 'react-device-detect';
import { PokerTheme } from '../../../Themes/PokerThemes';
import CardImage from './Card';
import PositionIndicator from './PositionIndicator';
import PlayerActionBadge from './PlayerActionBadge';
import PokerChip from './PokerChip';
import ActionTimerComponent from './ActionTimer';
import styles from '../../../assets/css/components/Aof.module.css';
import { IPlayerState } from '../../../Core/State/IPlayerState';
import ReactCountryFlag from "react-country-flag";
import playerSlotBackground from '/images/table/player-slot-background.svg';
import disconnectedIcon from '/images/table/disconnected.svg';
import winAnimation from '/images/table/win.gif';
import PendingBalanceBadge from './PendingBalanceBadge';
import '../../../assets/css/components/CardAnimations.css';
import { getCountryName } from '../../../Utils/CountryUtils';
import SitOnTableButton from '../../Common/SitOnTableButton';
import { ActionBadgeState, WinnerIndicatorState } from '../Presentation/PokerPlayerPresentation';
import { HAND_PRESENTATION_FADE_OUT_MS } from '../Presentation/PokerPresentationConfig';

export enum PlayerCardAnimationStyle {
    NONE = 'none',
    FLIP = 'flip',
    SLIDE = 'slide',
    SPREAD = 'spread',
    DEAL = 'deal'
}

interface PlayerSlotContainerProps extends BoxProps {
    issitout?: boolean;
    isFolded?: boolean;
    canOpenTableReactionPicker?: boolean;
    style?: React.CSSProperties;
    className?: string;
    onClick?: React.MouseEventHandler<HTMLDivElement>;
    'data-player-id'?: string;
    'data-poker-reaction-player-seat'?: string;
}

const PlayerSlotContainer = styled(Box, {
    shouldForwardProp: (prop) => !['issitout', 'isFolded', 'canOpenTableReactionPicker'].includes(prop as string)
})<PlayerSlotContainerProps>(({ theme, issitout, isFolded, canOpenTableReactionPicker }) => {
    const pokerTheme = theme as PokerTheme;
    return {
        position: 'absolute',
        width: '110px',
        height: '110px',
        backgroundImage: `url(${playerSlotBackground})`,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        alignItems: 'center',
        color: 'white',
        fontSize: '14px',
        cursor: !isMobile && canOpenTableReactionPicker ? 'grab' : 'default',
        padding: isMobile ? '10px 16px' : '0',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box',
    };
});

const OutText = styled(Typography)({
    backgroundColor: 'rgba(40, 40, 54, 0.70)',
    borderRadius: '50%',
    position: 'absolute',
    top: '2px',
    left: '2px',
    right: '2px',
    bottom: '2px',
    color: '#C1C1C1',
    fontSize: isMobile ? '12px' : '12px',
    fontWeight: 700,
    textAlign: 'center',
    lineHeight: isMobile ? '46px' : '66px'
});

const UserStatus = styled(Typography)({
    position: 'absolute',
    bottom: '24px',
    backgroundColor: '#646464',
    borderRadius: '30px',
    fontSize: '11px',
    fontWeight: 600,
    fontFamily: 'Inter, serif',
    color: 'white',
    whiteSpace: 'nowrap',
    padding: '0 5px',
    zIndex: 1
});

const PlayerAvatar = styled('img')<{ issitout?: boolean; isFolded?: boolean; src?: string | null, isPlayerTurn?: boolean, isClientPlayer?: boolean }>(({ issitout, isFolded, isPlayerTurn, isClientPlayer }) => ({
    width: isMobile ? '50px' : '70px',
    height: isMobile ? '50px' : '70px',
    borderRadius: '50%',
    boxSizing: 'border-box',
    stroke: 'red',
    border: '2px solid transparent',
    outline: isPlayerTurn ? '2px solid transparent' : `2px solid ${isClientPlayer ? '#F52740' : (issitout || isFolded) ? '#CDCDCD50' : '#CDCDCD'}`
}));

interface PlayerNameProps extends TypographyProps {
    colorindex: number;
    issitout?: boolean;
    isFolded?: boolean;
}

const PlayerName = styled(Typography, {
    shouldForwardProp: (prop) => !['colorindex', 'issitout', 'isFolded'].includes(prop as string)
})<PlayerNameProps>(({ theme, colorindex, issitout, isFolded }) => ({
    fontSize: '10px',
    fontWeight: '500',
    fontFamily: 'Inter, serif',
    color: 'white',
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    opacity: issitout || isFolded ? 0.6 : 1
}));

const PlayerCards = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    gap: isMobile ? '4px' : '8px',
    position: 'absolute',
    zIndex: 12,
}));

const balanceChangeAnimation = keyframes`
  0% { color: #FF0000; }
  50% { color: #FFFF00; }
  100% { color: #FF0000; }
`;

interface AnimatedBalanceProps extends TypographyProps {
    ischanging: number;
    issitout?: boolean;
    isFolded?: boolean;
}

const AnimatedBalance = styled(Typography, {
    shouldForwardProp: (prop) => !['ischanging', 'issitout', 'isFolded'].includes(prop as string)
})<AnimatedBalanceProps>(({ theme, ischanging, issitout, isFolded }) => {
    const pokerTheme = theme as PokerTheme;
    return {
        fontSize: '12px',
        fontFamily: 'Inter, serif',
        fontWeight: 600,
        width: '100%',
        textShadow: '0px 0px 2px rgba(0, 0, 0, 0.5)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        animation: ischanging ? `${balanceChangeAnimation} 500ms ease-in-out` : 'none',
        color: pokerTheme.poker.playerBalanceColor,
        opacity: issitout || isFolded ? 0.6 : 1
    };
});

const ChipContainer = styled(Stack)(({ theme }) => ({
    position: 'absolute',
    zIndex: 10,
}));

const withResponsiveSeatScale = (style: React.CSSProperties): React.CSSProperties => {
    if (isMobile) return style;

    const transform = style.transform
        ? `${style.transform} scale(var(--poker-seat-scale, 1))`
        : 'scale(var(--poker-seat-scale, 1))';

    return {
        ...style,
        transform,
        transformOrigin: 'center center'
    };
};

interface PlayerSlotProps {
    index: number;
    player: IPlayerState | null;
    isCurrentUserSeatedAnywhere: boolean;
    adjustedSeatIndex: number;
    isDealer: boolean;
    isBigBlind: boolean;
    isSmallBlind: boolean;
    isActiveGameRound: boolean;
    actionBadge: ActionBadgeState | undefined;
    winnerIndicator: WinnerIndicatorState | undefined;
    isClientPlayer: boolean;
    shouldShowCards: boolean;
    isPlayerTurn: boolean;
    balanceChanges: { [key: string]: number };
    formatCurrency: (amount: number) => string;
    handleSitButtonClick: (position: number) => void;
    getSlotPosition: (index: number) => React.CSSProperties;
    getSlotClassName: (index: number) => string;
    getPlayerButtonPosition: (index: number) => React.CSSProperties;
    getPlayerInfoStyles: (index: number) => {
        width: string;
        textAlign?: string;
    };
    getVisibleCardPosition: (index: number) => {
        top?: string;
        bottom?: string;
        left?: string;
        right?: string;
        transform?: string;
        marginTop?: string;
        marginBottom?: string;
        marginLeft?: string;
        marginRight?: string;
    };
    getShowdownCardPosition?: (index: number) => {
        top?: string;
        bottom?: string;
        left?: string;
        right?: string;
        transform?: string;
        marginTop?: string;
        marginBottom?: string;
        marginLeft?: string;
        marginRight?: string;
    };
    getCardPosition: (index: number) => {
        top?: string;
        bottom?: string;
        left?: string;
        right?: string;
        transform?: string;
        marginTop?: string;
        marginBottom?: string;
        marginLeft?: string;
        marginRight?: string;
    };
    getChipPosition: (index: number) => {
        top?: string;
        bottom?: string;
        left?: string;
        right?: string;
        transform?: string;
        marginTop?: string;
        marginBottom?: string;
    };
    getActionPosition: (index: number) => React.CSSProperties;
    activePlayerTimer: number | null;
    turnStartTime?: string;
    turnTime?: number;
    emptyIcon: string;
    sitHereIcon: string;
    userId?: string;
    cardAnimationStyle?: PlayerCardAnimationStyle;
    animationSpeed?: number;
    showdownCards?: Array<{
        username: string;
        show: boolean;
        cards: string[];
    }>;
    stateName?: string;
    type?: string;
    pendingBalanceUpdates?: Array<{
        playerId: string;
        amount: number;
    }>;
    playerEquities?: Array<{
        playerId: string;
        equity: number;
    }>;
    currencyType?: string;
    currency?: string;
    hideCurrentBetChipsThisHand?: boolean;
    isTournamentTable?: boolean;
    isHandPresentationFadingOut?: boolean;
    canOpenTableReactionPicker?: boolean;
    onOpenTableReactionPicker?: (player: IPlayerState, anchorEl: HTMLElement) => void;
}

const PlayerSlotComponent: React.FC<PlayerSlotProps> = ({
    index,
    player,
    isCurrentUserSeatedAnywhere,
    adjustedSeatIndex,
    isDealer,
    isBigBlind,
    isSmallBlind,
    isActiveGameRound,
    actionBadge,
    winnerIndicator,
    isClientPlayer,
    shouldShowCards,
    isPlayerTurn,
    balanceChanges,
    formatCurrency,
    handleSitButtonClick,
    getSlotPosition,
    getSlotClassName,
    getPlayerButtonPosition,
    getPlayerInfoStyles,
    getVisibleCardPosition,
    getShowdownCardPosition,
    getCardPosition,
    getChipPosition,
    getActionPosition,
    activePlayerTimer,
    turnStartTime,
    turnTime,
    emptyIcon,
    sitHereIcon,
    userId,
    cardAnimationStyle = PlayerCardAnimationStyle.FLIP,
    animationSpeed = 1,
    showdownCards,
    stateName,
    type,
    pendingBalanceUpdates,
    playerEquities,
    currencyType,
    currency,
    hideCurrentBetChipsThisHand,
    isTournamentTable,
    isHandPresentationFadingOut,
    canOpenTableReactionPicker,
    onOpenTableReactionPicker
}) => {
    const renderPlayerCards = (
        player: IPlayerState,
        isClientPlayer: boolean,
        cardAnimationStyle: PlayerCardAnimationStyle = PlayerCardAnimationStyle.FLIP,
        animationSpeed: number = 1,
        showdownCards?: Array<{
            username: string;
            show: boolean;
            cards: string[];
        }>,
        stateName?: string,
        type?: string
    ) => {
        const getCardType = (type: string) => {
            if (type === 'aof') {
                return '2B';
            }
            return '1B';
        };

        const getAnimationClasses = (index: number) => {
            const speedClass = animationSpeed === 1
                ? 'animation-speed-1'
                : animationSpeed > 1
                    ? 'animation-speed-2'
                    : 'animation-speed-0-5';

            const animationClass = `card-animation-${cardAnimationStyle.toLowerCase()}`;
            const delayClass = `card-delay-${index}`;

            return `animated-card ${animationClass} ${speedClass} ${delayClass}`;
        };

        // Handle Texas Hold'em game type
        if (type === 'texas') {

            // Handle showdown state first
            if (stateName === 'showdown' && showdownCards && player.username && player.isActiveInCurrentGame) {
                const showdownInfo = showdownCards.find(sc => sc.username === player.username);
                const isWinner = Boolean(winnerIndicator);
                if (showdownInfo?.show && showdownInfo.cards.length > 0) {
                    return showdownInfo.cards.map((card, index) => (
                        <div key={`player-card-${index}-${card}`} className={getAnimationClasses(index)}>
                            <CardImage
                                card={card}
                                small
                                highlighted={isWinner}
                                style={{
                                    width: isMobile ? '36px' : '60px',
                                    borderRadius: '4px',
                                    border: '2px solid transparent',
                                }}
                            />
                        </div>
                    ));
                }
            }

            // Don't show any cards if game is waiting for players
            if (stateName === 'waitingForPlayers' || stateName === 'waitingForMorePlayers') {
                return null;
            }

            // Show client player's cards
            if (isClientPlayer && player.cards && player.isActiveInCurrentGame) {
                return player.cards.map((card, index) => (
                    <div key={`player-card-${index}-${card}`} className={getAnimationClasses(index)}>
                        <CardImage card={card} medium style={{ width: isMobile ? '48px' : '60px' }} />
                    </div>
                ));
            }

            // Show hole cards for opponents
            if (!isClientPlayer && player.isActiveInCurrentGame) {
                return [
                    <div key="card1" className={getAnimationClasses(0)}>
                        <CardImage card={getCardType(type)} medium />
                    </div>,
                    <div
                        key="card2"
                        className={`${getAnimationClasses(1)} card-position-offset`}
                    >
                        <CardImage card={getCardType(type)} medium />
                    </div>
                ];
            }
        }

        if (type === 'aof') {
            // Don't show any cards if game is waiting for players
            if (stateName === 'waitingForPlayers' || stateName === 'waitingForMorePlayers') {
                return null;
            }

            // For client player during showdown, always show their cards
            if (stateName === 'showdown' && isClientPlayer && player.cards) {
                return player.cards.map((card, index) => (
                    <div key={`player-card-${index}-${card}`} className={getAnimationClasses(index)}>
                        <CardImage card={card} medium style={{ width: isMobile ? '48px' : '60px' }} />
                    </div>
                ));
            }

            // For other players in showdown, check showdown cards
            if (stateName === 'showdown' && showdownCards && player.username) {
                const showdownInfo = showdownCards.find(sc => sc.username === player.username);

                if (showdownInfo?.show && showdownInfo.cards.length > 0) {
                    return showdownInfo.cards.map((card, index) => (
                        <div key={`player-card-${index}-${card}`} className={getAnimationClasses(index)}>
                            <CardImage
                                card={card}
                                medium
                                style={{
                                    width: isMobile ? '24px' : '55px',
                                    borderRadius: '4px',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                    ));
                }
            }

            // Show player's own cards or hole cards for others
            if (isClientPlayer && player.cards) {
                return player.cards.map((card, index) => (
                    <div key={`player-card-${index}-${card}`} className={getAnimationClasses(index)}>
                        <CardImage card={card} medium style={{ width: isMobile ? '48px' : '60px' }} />
                    </div>
                ));
            } else if (stateName && !['waitingForPlayers', 'waitingForMorePlayers'].includes(stateName)) {
                // Show face down cards for other players
                return [
                    <div key="card1" className={getAnimationClasses(0)}>
                        <CardImage card={getCardType(type)} medium />
                    </div>,
                    <div
                        key="card2"
                        className={`${getAnimationClasses(1)} card-position-offset`}
                    >
                        <CardImage card={getCardType(type)} medium />
                    </div>
                ];
            }
        }

        return null;
    };

    const getPlayerEquity = (playerId: string) => {
        if (!playerEquities) return null;
        const equityData = playerEquities.find(eq => eq.playerId === playerId);
        return equityData ? equityData.equity : null;
    };

    const handPresentationFadeStyle: React.CSSProperties = {
        opacity: isHandPresentationFadingOut ? 0 : 1,
        transition: `opacity ${HAND_PRESENTATION_FADE_OUT_MS}ms ease-out`
    };
    const slotPositionStyle = withResponsiveSeatScale(getSlotPosition(adjustedSeatIndex));

    if (player) {
        const isAnySitOut = player.isSitOut || player.isTournamentSitOut;
        const seatStatusLabel = player.isDisconnected
            ? 'Disconnected'
            : isAnySitOut
                ? 'Sitting Out'
                : null;
        const seatStatusColor = player.isDisconnected ? '#FF6B6B' : '#F5A623';

        // Determine position indicators.
        //
        // IMPORTANT: In heads-up, the dealer is also the small blind, so we must
        // be able to show *both* indicators at the same time. Relying on a single
        // `player.position.type` value cannot represent that, so we use the
        // room-level ids (dealerId/smallBlindId/bigBlindId) passed in as booleans.
        const showDealer = isDealer;
        const showBigBlind = isBigBlind;
        const showSmallBlind = isSmallBlind;
        const showUTG = false;

        return (
            <PlayerSlotContainer
                key={index}
                style={slotPositionStyle}
                data-player-id={player.userId}
                data-poker-reaction-player-seat={player.userId}
                issitout={isAnySitOut}
                isFolded={player.hasFoldedInCurrentGame || player.isFolded}
                canOpenTableReactionPicker={canOpenTableReactionPicker}
                className={`${getSlotClassName(adjustedSeatIndex)}`}
                onClick={(event) => onOpenTableReactionPicker?.(player, event.currentTarget)}
            >
                {/* Position indicators - split left/right for heads-up (D + SB) */}
                {isActiveGameRound && showDealer && showSmallBlind ? (
                    <>
                        {/* Dealer on left */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: isMobile ? '-5px' : '-20px',
                                left: isMobile ? '0px' : '-10px',
                                zIndex: 20,
                                pointerEvents: 'none',
                            }}
                        >
                            <PositionIndicator
                                isDealer={true}
                                isBigBlind={false}
                                isSmallBlind={false}
                            />
                        </Box>
                        {/* Small Blind on right */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: isMobile ? '-5px' : '-20px',
                                right: isMobile ? '0px' : '-10px',
                                zIndex: 20,
                                pointerEvents: 'none',
                            }}
                        >
                            <PositionIndicator
                                isDealer={false}
                                isBigBlind={false}
                                isSmallBlind={true}
                            />
                        </Box>
                    </>
                ) : (
                    <Box
                        style={getPlayerButtonPosition(adjustedSeatIndex)}
                        sx={{
                            position: 'absolute',
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'flex-start',
                            zIndex: 20,
                            pointerEvents: 'none',
                        }}
                    >
                        {isActiveGameRound && (showDealer || showBigBlind || showSmallBlind || showUTG) && (
                            <PositionIndicator
                                isDealer={showDealer}
                                isBigBlind={showBigBlind}
                                isSmallBlind={showSmallBlind}
                            />
                        )}
                    </Box>
                )}
                {winnerIndicator && (
                    <img src={winAnimation} alt="Win animation" style={{
                        position: 'absolute',
                        top: isMobile ? '-32px' : '-51px',
                        left: '50%',
                        width: isMobile ? '94px' : '128px',
                        height: isMobile ? '94px' : '128px',
                        transform: 'translateX(-50%)',
                        zIndex: 25,
                        ...handPresentationFadeStyle
                    }} />
                )}
                <Box
                    sx={{ position: 'absolute', width: isMobile ? '50px' : '70px', height: isMobile ? '50px' : '70px', top: isMobile ? '-10px' : '-22px', left: '50%', transform: 'translate(-50%)', zIndex: 10 }}
                >
                    <PlayerAvatar
                        data-poker-reaction-player-avatar={player.userId}
                        className={styles.avatar}
                        src={player.avatarUrl || undefined}
                        alt={player.username}
                        issitout={isAnySitOut}
                        isFolded={player.hasFoldedInCurrentGame || player.isFolded}
                        isPlayerTurn={isPlayerTurn}
                        isClientPlayer={isClientPlayer}
                    />
                    {(player.hasFoldedInCurrentGame || player.isFolded || isAnySitOut) && (
                        <OutText>OUT</OutText>
                    )}
                    {(player.isDisconnected) && (
                        <OutText sx={{ lineHeight: isMobile ? '54px' : '74px' }}><img src={disconnectedIcon} style={{ marginTop: '5px' }} alt="Disconnect icon" /></OutText>
                    )}
                </Box>
                {player.countryCode && (
                    <Tooltip title={getCountryName(player.countryCode)}>
                        <div style={{
                            position: 'absolute',
                            top: isMobile ? '24px' : '20px',
                            left: '10px',
                            width: '22px',
                            height: '15px',
                            borderRadius: '2px',
                            zIndex: 20
                        }}>
                            <ReactCountryFlag
                                countryCode={player.countryCode}
                                svg
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '2px'
                                }}
                            />
                        </div>
                    </Tooltip>
                )}
                <Stack alignItems="center" sx={{ width: '90%', margin: '0 5%', textAlign: 'center', marginTop: '30px' }}>
                    <PlayerName
                        colorindex={adjustedSeatIndex}
                        issitout={isAnySitOut}
                        isFolded={player.hasFoldedInCurrentGame || player.isFolded}
                        className={isMobile && isClientPlayer ? styles.mobilePlayerName : ''}
                    >
                        {player.username} {isMobile && isClientPlayer && (<span>(You)</span>)}
                    </PlayerName>
                    {seatStatusLabel && (
                        <UserStatus>{seatStatusLabel}</UserStatus>
                    )}
                    {player.balance !== undefined && (
                        <AnimatedBalance
                            ischanging={balanceChanges[player.userId] ? 1 : 0}
                            issitout={isAnySitOut}
                            isFolded={player.hasFoldedInCurrentGame || player.isFolded}
                        >
                            {/* {sitOptions?.currencyType === 'crypto' && (
                                <img 
                                    src={fundsChipIcon} 
                                    alt="Crypto Icon" 
                                    style={{ 
                                        width: '14px',
                                        height: '14px',
                                        marginRight: '2px'
                                    }} 
                                />
                            )} */}
                            {player.balance !== null && formatCurrency(Number(player.balance))}
                        </AnimatedBalance>
                    )}
                </Stack>

                {player.hasFoldedInCurrentGame || player.isFolded && (
                    <Typography
                        sx={{
                            position: 'absolute',
                            top: isMobile ? '-24px' : '-36px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            color: '#3D3B40',
                            backgroundColor: '#F52740',
                            fontSize: '10px',
                            padding: '2px 8px',
                            borderRadius: '30px',
                            fontWeight: 700,
                            textTransform: 'capitalize',
                            fontFamily: '"Inter", serif',
                            whiteSpace: 'nowrap',
                            overflow: 'visible',
                            zIndex: 20,
                        }}
                    >
                        Fold
                    </Typography>
                )}
                {/* {!(player.hasFoldedInCurrentGame || player.isFolded) && (shouldShowCards || Boolean(showdownCards?.length)) && (() => { */}
                {(shouldShowCards || Boolean(showdownCards?.length)) && (() => {
                    const showdownInfo = showdownCards?.find(sc => sc.username === player.username);
                    return (
                        <PlayerCards className={isPlayerTurn && turnTime && activePlayerTimer !== null && activePlayerTimer <= 5 ? styles.shake : undefined} style={{
                            ...(isClientPlayer && player.cards
                                ? getVisibleCardPosition(adjustedSeatIndex)
                                : showdownInfo?.show && showdownInfo.cards.length > 0
                                    ? (getShowdownCardPosition || getCardPosition)(adjustedSeatIndex)
                                    : getCardPosition(adjustedSeatIndex)),
                            ...handPresentationFadeStyle,
                            opacity: (player.hasFoldedInCurrentGame || player.isFolded) ? 0.5 : 1
                        }}>
                            {renderPlayerCards(player, isClientPlayer, cardAnimationStyle, animationSpeed, showdownCards, stateName, type)}
                        </PlayerCards>
                    );
                })()}
                {winnerIndicator ? (
                    <PlayerActionBadge
                        action="WIN"
                        amount={winnerIndicator.amount}
                        position={{
                            ...getActionPosition(adjustedSeatIndex),
                            ...handPresentationFadeStyle
                        }}
                        currencyType={currencyType}
                        currency={currency}
                    />
                ) : actionBadge && actionBadge.action !== 'ante' && (
                    <PlayerActionBadge
                        action={actionBadge.action}
                        amount={actionBadge.amount}
                        position={getActionPosition(adjustedSeatIndex)}
                        currencyType={currencyType}
                        currency={currency}
                    />
                )}
                {/* Main bet chip display - for all game types */}
                {/* For Texas, only show if hideCurrentBetChipsThisHand is false */}
                {/* For AoF and other types, always show */}
                {((type !== 'texas') || (type === 'texas' && !hideCurrentBetChipsThisHand)) && 
                 player.currentBet !== undefined && 
                 player.currentBet !== null && 
                 player.currentBet >= 0.01 && (
                    <ChipContainer 
                        direction="row" 
                        style={isMobile && isClientPlayer ? 
                            { top: '-124px', left: '50%', transform: 'translateX(-50%)', zIndex: 11 } : 
                            getChipPosition(adjustedSeatIndex)}
                    >
                        <PokerChip
                            amount={player.currentBet}
                            currency={currencyType === 'crypto' ? `${currency} ` : currencyType === 'tournament_chips' ? '' : '$'}
                            suffix={''}
                        />
                    </ChipContainer>
                )}

                {isPlayerTurn && turnTime && activePlayerTimer !== null && (
                    <ActionTimerComponent
                        className={styles.actionTimer}
                        key={`timer-${turnStartTime}`}
                        initialTime={turnTime}
                        currentTime={activePlayerTimer}
                        turnStartTime={turnStartTime || ''}
                        isMobileUser={isMobile && isClientPlayer}
                    />
                )}
                {player && pendingBalanceUpdates && pendingBalanceUpdates.some(update => update.playerId === player.userId) && (
                    <Box sx={{ position: 'absolute', zIndex: 999, width: '100%', height: '100%' }}>
                        {pendingBalanceUpdates.map((update, i) =>
                            update.playerId === player.userId && (
                                <PendingBalanceBadge
                                    key={i}
                                    amount={update.amount}
                                    position={{
                                        top: '92px',
                                        left: '50%',
                                        transform: 'translateX(-50%)'
                                    }}
                                />
                            )
                        )}
                    </Box>
                )}
                {/* {playerEquities && getPlayerEquity(player.userId) !== null && (
                    <EquityBadge
                        equity={getPlayerEquity(player.userId)!}
                        position={{
                            top: '-5px',
                            right: '-20px',
                        }}
                    />
                )} */}
            </PlayerSlotContainer>
        );
    } else {
        return (
            <PlayerSlotContainer
                key={index}
                style={slotPositionStyle}
                onClick={() => {
                    if (!isCurrentUserSeatedAnywhere && !isTournamentTable) {
                        handleSitButtonClick(adjustedSeatIndex);
                    }
                }}
                className={`${isCurrentUserSeatedAnywhere ? styles.empty : isTournamentTable ? styles.empty : styles.sitHere} ${getSlotClassName(adjustedSeatIndex)}`}
            >
                {isCurrentUserSeatedAnywhere ? (
                    <img src={emptyIcon} className={isMobile ? styles.mobileSeatButton : styles.emptySeatButton} width={110} height={110} alt="Empty seat icon" />
                ) : isTournamentTable ? (
                    <img src={emptyIcon} className={isMobile ? styles.mobileSeatButton : styles.emptySeatButton} width={110} height={110} alt="Empty seat icon" />
                ) : (
                    <SitOnTableButton size={isMobile ? 'medium' : 'large'} className={isMobile ? styles.mobileSeatButton : styles.seatButton} />
                )}
            </PlayerSlotContainer>
        );
    }
};

export default PlayerSlotComponent;
